/**
 * Reading an exported package zip in the browser (the Preview panel unzips what the API's export returns; ADR
 * 0020). Stored and deflated entries, no zip64, CRC-checked; deflate goes through DecompressionStream.
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Inflate at most `max` bytes (the entry's declared size): a zip bomb stops there instead of filling memory. */
async function inflateRaw(data: Uint8Array, max: number): Promise<Uint8Array> {
  const reader = new Blob([data as Uint8Array<ArrayBuffer>]).stream().pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  const out = new Uint8Array(max);
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (n + value.length > max) {
      await reader.cancel().catch(() => undefined);
      throw new Error('an entry inflates past its declared size');
    }
    out.set(value, n);
    n += value.length;
  }
  return n === max ? out : out.subarray(0, n);
}

/** Limits for packages opened in the browser (the preview, the read-only view). */
export const LIMITS = { maxEntries: 4096, maxEntryBytes: 256 * 1024 * 1024, maxTotalBytes: 512 * 1024 * 1024 };

/** A safe relative path: no absolute path, drive letter, backslash, control character, "." or ".." segment. */
export function safeZipPath(name: string): boolean {
  if (!name || name.length > 255 || name.startsWith('/') || /[\\\u0000-\u001f\u007f]/.test(name) || /^[A-Za-z]:/.test(name)) return false;
  const segs = name.replace(/\/$/, '').split('/');
  return segs.length <= 16 && segs.every((x) => x !== '' && x !== '.' && x !== '..');
}

/**
 * Path → bytes for every file in the zip (directories skipped). Untrusted input: unsafe names, symbolic links,
 * duplicates, too many entries and oversized entries are refused before anything is inflated.
 */
export async function unzip(zip: Uint8Array): Promise<Map<string, Uint8Array>> {
  const view = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);
  let eocd = -1;
  for (let i = zip.length - 22; i >= Math.max(0, zip.length - 22 - 65535); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('not a zip file (no end of central directory)');
  const count = view.getUint16(eocd + 10, true);
  if (count > LIMITS.maxEntries) throw new Error(`too many entries (${count})`);
  let p = view.getUint32(eocd + 16, true);
  let total = 0;
  const names = new TextDecoder();
  const out = new Map<string, Uint8Array>();
  for (let n = 0; n < count; n++) {
    if (p + 46 > zip.length || view.getUint32(p, true) !== 0x02014b50) throw new Error('corrupt central directory');
    const madeBy = view.getUint8(p + 5);
    const method = view.getUint16(p + 10, true);
    const crc = view.getUint32(p + 16, true);
    const compressed = view.getUint32(p + 20, true);
    const size = view.getUint32(p + 24, true);
    const external = view.getUint32(p + 38, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    const name = names.decode(zip.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (!safeZipPath(name)) throw new Error(`unsafe entry name ${JSON.stringify(name.slice(0, 80))}`);
    if (madeBy === 3 && ((external >>> 16) & 0o170000) === 0o120000) throw new Error(`symbolic link ${name}`);
    if (out.has(name)) throw new Error(`duplicate entry ${name}`);
    if (size === 0xffffffff || compressed === 0xffffffff || size > LIMITS.maxEntryBytes) throw new Error(`entry ${name} is too large`);
    total += size;
    if (total > LIMITS.maxTotalBytes) throw new Error('the package unpacks to too many bytes');
    if (local + 30 > zip.length || view.getUint32(local, true) !== 0x04034b50) throw new Error(`corrupt local header for ${name}`);
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    if (start + compressed > zip.length) throw new Error(`truncated entry ${name}`);
    const body = zip.subarray(start, start + compressed);
    let bytes: Uint8Array;
    if (method === 0) bytes = body.slice();
    else if (method === 8) bytes = await inflateRaw(body, size);
    else throw new Error(`unsupported compression method ${method} for ${name}`);
    if (bytes.length !== size) throw new Error(`entry ${name} is not its declared size`);
    if (crc32(bytes) !== crc) throw new Error(`CRC mismatch for ${name}`);
    if (!name.endsWith('/')) out.set(name, bytes);
  }
  return out;
}

export interface PackageFiles {
  id: string;
  manifestPath: string;
  /** Path inside the package (without the `<id>/` folder) → bytes. */
  files: Map<string, Uint8Array>;
  /** Sum of the uncompressed file sizes. */
  unpackedBytes: number;
}

/** The package inside an exported zip (`<id>/<id>.ograf.json`, …), with paths relative to the package folder. */
export async function readPackageZip(zip: Uint8Array): Promise<PackageFiles> {
  const all = await unzip(zip);
  const manifest = [...all.keys()].find((k) => /^[^/]+\/[^/]+\.ograf\.json$/.test(k) || /^[^/]+\.ograf\.json$/.test(k));
  if (!manifest) throw new Error('no *.ograf.json in the package');
  const root = manifest.includes('/') ? manifest.slice(0, manifest.indexOf('/') + 1) : '';
  const files = new Map<string, Uint8Array>();
  let unpackedBytes = 0;
  for (const [k, v] of all) {
    if (!k.startsWith(root)) continue;
    files.set(k.slice(root.length), v);
    unpackedBytes += v.length;
  }
  return { id: root.replace(/\/$/, '') || manifest.replace(/\.ograf\.json$/, ''), manifestPath: manifest.slice(root.length), files, unpackedBytes };
}
